<svelte:options runes={false} />
<script>
  // Lifecycle controls (U8): pause / resume / end (stop) / remove (undeploy), mapped onto the server state
  // machine (KTD5). Buttons are enabled ONLY for legal transitions from the current status; intermediate
  // states disable everything with a progressive label. The flatten-vs-leave-managed disposition for stop /
  // undeploy is surfaced INSIDE a confirm dialog (not a missable inline toggle), with the primary button
  // reflecting the choice. A double-submit guard prevents re-clicking mid-transition.
  import { createEventDispatcher } from 'svelte';
  import { isCrossTenantOrgContext, ownUserId } from '../../lib/orgContext.js';

  const dispatch = createEventDispatcher();

  // The strategy's owner (snapshot userId). The server accepts lifecycle triggers from the OWNER
  // regardless of the selected org context (the AutoTrader host gates on claims, not the org
  // override), so the read-only lockout below must never fire for the owner — StrategyWorkspace
  // auto-selects a participating org for multi-org strategies, which would otherwise hide working
  // controls from the owner on their own strategy (2026-08-31 review).
  export let owner = null;
  $: ownedByViewer = !!owner && !!$ownUserId && owner === $ownUserId;
  $: lockedForOrgContext = $isCrossTenantOrgContext && !ownedByViewer;
  // An already-open confirm dialog must not survive the context flipping read-only under it.
  $: if (lockedForOrgContext) confirmOpen = false;

  export let status = null;       // Deployed | Starting | Running | Pausing | Paused | Stopping | Stopped | Faulted | Undeploying | Undeployed
                                  // ('Undeployed' is terminal — the backend drops it from the list, so it never renders here;
                                  //  'Undeploying' renders with a "Removing…" chip until the supervisor finalizes the removal.)
  export let busy = false;        // an action is in flight (host sets this around the API call)
  // The deploy-time candle-backfill state (orthogonal to status). Start is gated on this being 'ready' — a
  // strategy can't run until its candles exist (the live loop would halt on an empty newest window).
  export let backfillState = 'ready'; // preparing | ready | failed | none
  export let backfillMessage = null;
  // Live candle-collection position (TASK-20260728 AC15): non-null only while a deploy backfill or
  // Start warmup is actively fetching — { symbol, symbolsDone, symbolsTotal, batchesDone, batchesTotal }.
  export let collection = null;

  $: collectionDetail = collection?.symbol
    ? ` — collecting ${collection.symbol}` +
      (collection.symbolsTotal > 1 ? ` (${(collection.symbolsDone ?? 0) + 1} of ${collection.symbolsTotal})` : '') +
      (collection.batchesTotal > 0 ? ` · batch ${collection.batchesDone} of ${collection.batchesTotal}` : '')
    : '';

  const INTERMEDIATE = ['Starting', 'Pausing', 'Stopping', 'Undeploying'];
  $: isIntermediate = INTERMEDIATE.includes(status);
  $: backfillPending = backfillState === 'preparing';
  $: backfillFailed = backfillState === 'failed';
  $: disabled = busy || isIntermediate;

  // Legal-transition enablement (mirrors the slice-4 state machine). Start additionally requires the candle
  // backfill to be ready — you can't run a strategy whose candles haven't been fetched yet.
  $: canStart = status === 'Deployed' && (backfillState === 'ready' || backfillState === 'none');
  $: canPause = status === 'Running';
  $: canResume = status === 'Paused';
  $: canStop = status === 'Running' || status === 'Pausing';
  $: canRemove = status === 'Deployed' || status === 'Stopped' || status === 'Faulted';

  // Progressive label for the in-flight intermediate state.
  $: progressiveLabel =
    status === 'Starting' ? 'Starting…' :
    status === 'Pausing' ? 'Pausing…' :
    status === 'Stopping' ? 'Stopping…' :
    status === 'Undeploying' ? 'Removing…' : null;

  // ---- Confirm dialog (stop / remove require a disposition + an explicit confirm) ----
  let confirmOpen = false;
  let confirmAction = null; // 'stop' | 'remove'
  let flatten = false;      // radio: false = leave managed (default), true = flatten at market
  let triggerEl = null;     // return focus here on close

  function openConfirm(action, event) {
    confirmAction = action;
    flatten = false;
    triggerEl = event?.currentTarget ?? null;
    confirmOpen = true;
  }
  function closeConfirm() {
    confirmOpen = false;
    confirmAction = null;
    if (triggerEl && typeof triggerEl.focus === 'function') triggerEl.focus();
  }
  function confirm() {
    const action = confirmAction;
    const flattenChoice = flatten;
    closeConfirm();
    dispatch(action, { flatten: flattenChoice }); // 'stop' | 'remove'
  }

  // Focus trap + Escape for the confirm dialog.
  let dialogEl = null;
  function onDialogKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeConfirm();
      return;
    }
    if (e.key !== 'Tab' || !dialogEl) return;
    const focusable = dialogEl.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  // Autofocus the dialog's first control when it opens.
  function focusDialog(node) {
    const btn = node.querySelector('button, input');
    if (btn) btn.focus();
    return {};
  }
</script>

<!-- Another org's strategies are read-only for NON-owners (TASK-20260830 AC 4): lifecycle control is
     owner-only on the server (404 for a member), so the buttons are replaced with the reason, not
     offered and refused. The owner keeps working controls in any org context. -->
{#if lockedForOrgContext}
  <div class="at-lifecycle-controls" role="note" data-testid="lifecycle-org-readonly">
    <span class="at-progressive">Viewing another organization — strategy control stays with its owner.</span>
  </div>
{:else}
<div class="at-lifecycle-controls" role="group" aria-label="Strategy lifecycle controls">
  {#if progressiveLabel}
    <span class="at-progressive" aria-live="polite"><span class="spinner"></span>
      {progressiveLabel}{status === 'Starting' ? collectionDetail : ''}</span>
  {/if}

  <!-- Backfill state (orthogonal to lifecycle): Start is disabled until candles are ready. -->
  {#if backfillPending}
    <span class="at-backfill at-backfill-preparing" role="status" aria-live="polite">
      <span class="spinner"></span> Backfilling candles…{collectionDetail}
    </span>
  {:else if backfillFailed}
    <span class="at-backfill at-backfill-failed" role="alert">
      ⚠ Backfill failed{backfillMessage ? ` — ${backfillMessage}` : ''}
    </span>
  {/if}

  <button type="button" class="btn btn-primary" disabled={disabled || !canStart}
    title={backfillPending ? 'Waiting for candle backfill to finish' : backfillFailed ? 'Candle backfill failed — cannot start' : undefined}
    on:click={() => dispatch('start')}>Start</button>

  <button type="button" class="btn btn-ghost" disabled={disabled || !canPause}
    on:click={() => dispatch('pause')}>Pause</button>

  <button type="button" class="btn btn-ghost" disabled={disabled || !canResume}
    on:click={() => dispatch('resume')}>Resume</button>

  <button type="button" class="btn btn-danger" disabled={disabled || !canStop}
    on:click={(e) => openConfirm('stop', e)}>End</button>

  <button type="button" class="btn btn-ghost at-remove" disabled={disabled || !canRemove}
    on:click={(e) => openConfirm('remove', e)}>Remove</button>
</div>
{/if}

{#if confirmOpen}
  <!-- Disposition confirm dialog: focus-trapped, Escape-dismissable, returns focus to the trigger. -->
  <div class="at-modal-backdrop" on:click|self={closeConfirm}>
    <div class="at-modal" role="dialog" aria-modal="true" aria-labelledby="at-confirm-title"
      bind:this={dialogEl} on:keydown={onDialogKeydown} use:focusDialog>
      <h3 id="at-confirm-title" class="at-modal-title">
        {confirmAction === 'stop' ? 'End strategy' : 'Remove strategy'}
      </h3>
      <p class="at-modal-body">
        {confirmAction === 'stop'
          ? 'Stop this running strategy. Choose what happens to any open positions.'
          : 'Remove (undeploy) this strategy and archive it. Choose what happens to any open positions.'}
      </p>
      <div class="at-disposition" role="radiogroup" aria-label="Open-position disposition">
        <label class="at-radio">
          <input type="radio" name="disposition" value="leave" checked={!flatten}
            on:change={() => (flatten = false)} />
          <span>Leave positions managed (default)</span>
        </label>
        <label class="at-radio">
          <input type="radio" name="disposition" value="flatten" checked={flatten}
            on:change={() => (flatten = true)} />
          <span>Flatten open positions at market</span>
        </label>
      </div>
      <div class="at-modal-actions">
        <button type="button" class="btn btn-ghost" on:click={closeConfirm}>Cancel</button>
        <button type="button" class="btn {flatten ? 'btn-danger' : 'btn-primary'}" on:click={confirm}>
          {#if confirmAction === 'stop'}
            {flatten ? 'End — flatten now' : 'End — leave managed'}
          {:else}
            {flatten ? 'Remove — flatten now' : 'Remove — leave managed'}
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

